import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useInvitationService } from '../services/invitation.service';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/atoms/Button';
import { useAuth0 } from '@auth0/auth0-react';

type InvitationParams = {
  token: string;
};

function InvitationAcceptPage() {
  const { token } = useParams() as InvitationParams;
  const navigate = useNavigate();
  const { validateInvitation } = useInvitationService();
  const { loginWithRedirect } = useAuth0();
  const { t } = useTranslation('auth');
  
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [email, setEmail] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);
  const [invalidReason, setInvalidReason] = useState('');
  const [userExists, setUserExists] = useState(false);
  const validationInProgress = useRef(false);
  
  // First, validate the token
  useEffect(() => {
    const validateToken = async () => {
      if (!token || validationInProgress.current) {
        setIsValidating(false);
        return;
      }
      
      validationInProgress.current = true;
            
      try {
        const invitation = await validateInvitation(token);
        
        // Check if invitation has expired
        const expirationDate = new Date(invitation.expires_at);
        if (expirationDate < new Date()) {
          toast.error(t('toasts.invitation_expired'));
          setInvalidReason('This invitation has expired.');
          setIsValid(false);
          return;
        }
        
        // Check if invitation is already accepted
        if (invitation.is_accepted) {
          toast.info(t('toasts.invitation_already_accepted'));
          setInvalidReason('This invitation has already been accepted.');
          setIsValid(false);
          return;
        }
        
        setIsValid(true);
        setEmail(invitation.email);
        setUserExists(invitation.user_exists);
      } catch (error) {
        console.error('Invalid invitation token:', error);
        toast.error(t('toasts.invitation_invalid'));
        setInvalidReason('This invitation link is invalid or has expired.');
        setIsValid(false);
      } finally {
        setIsValidating(false);
        validationInProgress.current = false;
      }
    };
    
    validateToken();
  }, [token]); // Remove validateInvitation from dependencies
  
  const handleAcceptInvitation = async () => {
    if (!token || !isValid || isAccepting) return;
    
    setIsAccepting(true);
    try {
      // Redirect to Auth0 with the invitation token stored in appState
      // If user already exists, redirect to login; otherwise, redirect to signup
      loginWithRedirect({
        appState: {
          returnTo: `/invite/${token}/complete`, // Route for post-auth completion
          invitationToken: token, // Store the token to use after auth
        },
        authorizationParams: {
          screen_hint: userExists ? 'login' : 'signup',
          login_hint: email,
        },
      });
    } catch (error) {
      console.error('Failed to initiate auth:', error);
      toast.error(t('toasts.auth_start_failed'));
      setIsAccepting(false);
    }
  };

  // Loading state
  if (isValidating) {
    return (
      <Layout className="bg-white" hideFooter={true}>
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent border-purple mb-3"></div>
          <p className="text-dark-invirtus font-body">{t('invitation.validating')}</p>
        </div>
      </Layout>
    );
  }

  // Invalid token
  if (!isValid) {
    return (
      <Layout className="bg-white" hideFooter={true}>
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <svg className="w-12 h-12 text-red mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <h1 className="text-xl font-semibold text-dark-invirtus mb-2 font-heading">{t('invitation.invalid_title')}</h1>
          <p className="text-dark-invirtus mb-4 text-center font-body">{invalidReason || t('invitation.invalid_desc')}</p>
          <Button
            label={t('invitation.go_to_dashboard')}
            onClick={() => navigate('/dashboard')}
            intent="primary"
            type="contained"
            id="invalid-invitation-home-btn"
          />
        </div>
      </Layout>
    );
  }

  // Valid token - show accept button
  return (
    <Layout className="bg-white" hideFooter={true}>
      <div className="flex flex-col items-center justify-center py-16 px-4 max-w-md mx-auto">
        <svg className="w-14 h-14 text-purple mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        
        <h1 className="text-2xl font-semibold text-dark-invirtus mb-2 font-heading">{t('invitation.invited_title')}</h1>
        
        <p className="text-dark-invirtus mb-1 font-body">{t('invitation.welcome', { email: <span className="text-purple font-medium font-body">{email}</span> })}</p>
        <p className="text-dark-invirtus mb-4 text-center font-body">
          {userExists ? t('invitation.existing_user') : t('invitation.new_user')}
        </p>
        
        <Button
          label={isAccepting
            ? (userExists ? t('invitation.sign_in_redirect') : t('invitation.signup_redirect'))
            : (userExists ? t('invitation.sign_in_to_continue') : t('invitation.accept'))}
          onClick={handleAcceptInvitation}
          disabled={isAccepting}
          loading={isAccepting}
          icon={!isAccepting ? ( <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"> <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/> </svg> ) : undefined}
          intent="primary"
          type="contained"
          className="w-full"
          id="accept-invitation-btn"
        />
      </div>
    </Layout>
  );
}

export default InvitationAcceptPage;